import { PrinterOutlined } from '@ant-design/icons';
import {
  Alert,
  App,
  Button,
  Checkbox,
  DatePicker,
  Empty,
  Flex,
  Modal,
  Pagination,
  Select,
  Spin,
  Table,
  Typography,
} from 'antd';
import dayjs from 'dayjs';
import { AdminContext } from 'constants/contexts';
import useFetchedData from 'hooks/useFetchedData';
import React, {
  useCallback, useContext, useEffect, useMemo, useState,
} from 'react';
import { Link } from 'react-router-dom';
import http from 'utils/http';
import {
  getBlobHttpErrorMessage,
  openBlankPrintTab,
  openPdfResponseInTab,
} from './kitchenSheetDownload';
import useCateringRestaurants from './useCateringRestaurants';
import './prep-sheet.css';

const { RangePicker } = DatePicker;

const HANDOFF_PAGE_SIZE = 10;
const HANDOFF_PRINT_MAX_ORDERS = 75;

function HandoffSheets() {
  const { message } = App.useApp();
  const { organizationId } = useContext(AdminContext);
  const [dateRange, setDateRange] = useState(() => [dayjs(), dayjs()]);
  const [restaurantIds, setRestaurantIds] = useState([]);
  const [page, setPage] = useState(1);
  const [printingOrderId, setPrintingOrderId] = useState(null);
  const [printAllLoading, setPrintAllLoading] = useState(false);
  const [printPreviewOpen, setPrintPreviewOpen] = useState(false);
  const [printPreviewLoading, setPrintPreviewLoading] = useState(false);
  const [printPreviewOrders, setPrintPreviewOrders] = useState([]);
  const [selection, setSelection] = useState(() => ({
    all: false,
    picked: new Set(),
    skipped: new Set(),
  }));

  const filterQuery = useMemo(() => ({
    start_date: dateRange?.[0] ? dateRange[0].format('YYYY-MM-DD') : undefined,
    end_date: dateRange?.[1] ? dateRange[1].format('YYYY-MM-DD') : undefined,
    restaurant_ids: restaurantIds.length ? restaurantIds.join(',') : undefined,
    tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
  }), [dateRange, restaurantIds]);

  const listQuery = useMemo(() => ({
    ...filterQuery,
    page,
    page_size: HANDOFF_PAGE_SIZE,
  }), [filterQuery, page]);

  const filterError = useMemo(() => {
    if (dateRange?.[0] && dateRange?.[1] && dateRange[0].isAfter(dateRange[1], 'day')) {
      return 'Start date must be on or before the end date.';
    }
    return null;
  }, [dateRange]);

  const handoffParams = organizationId && !filterError ? {
    url: `v1/organizations/${organizationId}/handoff-sheets/`,
    query: listQuery,
  } : null;

  const {
    data: handoffData,
    loading: handoffLoading,
    error: handoffError,
  } = useFetchedData(
    handoffParams,
    (response = {}) => ({
      orders: response?.data?.results || response?.results || response?.data || [],
      totalCount: response?.data?.count ?? response?.count ?? 0,
    }),
  );

  const { restaurantOptions } = useCateringRestaurants();
  const orders = filterError ? [] : (handoffData?.orders || []);
  const totalCount = filterError ? 0 : (handoffData?.totalCount ?? orders.length);
  const selectedCount = selection.all
    ? Math.max(0, totalCount - selection.skipped.size)
    : selection.picked.size;
  const isOrderSelected = useCallback((orderId) => (
    selection.all ? !selection.skipped.has(orderId) : selection.picked.has(orderId)
  ), [selection]);
  const printTooManyOrders = selectedCount > HANDOFF_PRINT_MAX_ORDERS;
  const printLimitMessage = `Print covers at most ${HANDOFF_PRINT_MAX_ORDERS} orders (${selectedCount} selected). Uncheck some orders or narrow the date range.`;

  useEffect(() => {
    setPage(1);
    setSelection({ all: false, picked: new Set(), skipped: new Set() });
  }, [dateRange, restaurantIds]);

  const setOrdersSelected = useCallback((orderIds, selected) => {
    setSelection((current) => {
      const picked = new Set(current.picked);
      const skipped = new Set(current.skipped);
      orderIds.forEach((orderId) => {
        if (current.all) {
          if (selected) {
            skipped.delete(orderId);
          } else {
            skipped.add(orderId);
          }
        } else if (selected) {
          picked.add(orderId);
        } else {
          picked.delete(orderId);
        }
      });
      return { all: current.all, picked, skipped };
    });
  }, []);

  const openHandoffPdf = useCallback(async (request, printTab) => {
    const response = await request;
    if (!openPdfResponseInTab(response, printTab)) {
      message.warning('Allow pop-ups for this site to open the handoff sheet, or try again.');
    }
  }, [message]);

  const handlePrintOrder = useCallback(async (orderId) => {
    if (!orderId || printingOrderId) {
      return;
    }
    const printTab = openBlankPrintTab();
    setPrintingOrderId(orderId);
    try {
      await openHandoffPdf(http.get(
        `v1/orders/${orderId}/handoff-sheet/`,
        {
          params: { tz: Intl.DateTimeFormat().resolvedOptions().timeZone },
          responseType: 'blob',
        },
      ), printTab);
    } catch (error) {
      printTab?.close();
      const errorMessage = await getBlobHttpErrorMessage(error);
      message.error(errorMessage || 'Unable to generate the handoff sheet');
    } finally {
      setPrintingOrderId(null);
    }
  }, [message, openHandoffPdf, printingOrderId]);

  const fetchAllHandoffOrders = useCallback(async () => {
    const pageSize = 100;
    let pageNum = 1;
    let allOrders = [];
    let total = 0;

    while (true) {
      const response = await http.get(
        `v1/organizations/${organizationId}/handoff-sheets/`,
        {
          params: {
            ...filterQuery,
            page: pageNum,
            page_size: pageSize,
          },
        },
      );
      const data = response?.data?.data || response?.data || {};
      const batch = data?.results ?? data?.orders ?? (Array.isArray(data) ? data : []);
      total = data?.count ?? batch.length;
      allOrders = allOrders.concat(batch);
      if (allOrders.length >= total || batch.length < pageSize) {
        break;
      }
      pageNum += 1;
    }
    return allOrders;
  }, [filterQuery, organizationId]);

  const openPrintPreview = useCallback(async () => {
    if (filterError) {
      message.warning(filterError);
      return;
    }
    if (printTooManyOrders) {
      message.warning(printLimitMessage);
      return;
    }
    if (!selectedCount || printPreviewLoading) {
      return;
    }
    setPrintPreviewOrders([]);
    setPrintPreviewOpen(true);
    setPrintPreviewLoading(true);
    try {
      const allOrders = await fetchAllHandoffOrders();
      const chosen = allOrders.filter((order) => isOrderSelected(order.order_id));
      setPrintPreviewOrders(chosen);
      if (!chosen.length) {
        setPrintPreviewOpen(false);
        message.warning('No orders selected to print.');
      }
    } catch (error) {
      setPrintPreviewOpen(false);
      const errorMessage = await getBlobHttpErrorMessage(error);
      message.error(errorMessage || 'Unable to load orders for print');
    } finally {
      setPrintPreviewLoading(false);
    }
  }, [
    isOrderSelected,
    fetchAllHandoffOrders,
    filterError,
    message,
    printLimitMessage,
    printPreviewLoading,
    printTooManyOrders,
    selectedCount,
  ]);

  const confirmPrintAll = useCallback(async () => {
    if (!printPreviewOrders.length || printAllLoading) {
      return;
    }
    const printTab = openBlankPrintTab();
    setPrintAllLoading(true);
    try {
      await openHandoffPdf(http.get(
        `v1/organizations/${organizationId}/handoff-sheets/`,
        {
          params: {
            ...filterQuery,
            export: 'pdf',
            order_ids: printPreviewOrders.map((order) => order.order_id).join(','),
          },
          responseType: 'blob',
        },
      ), printTab);
      setPrintPreviewOpen(false);
    } catch (error) {
      printTab?.close();
      const errorMessage = await getBlobHttpErrorMessage(error);
      message.error(errorMessage || 'Unable to generate handoff sheets');
    } finally {
      setPrintAllLoading(false);
    }
  }, [
    filterQuery,
    message,
    openHandoffPdf,
    organizationId,
    printAllLoading,
    printPreviewOrders,
  ]);

  const previewColumns = useMemo(() => [
    {
      title: 'Order',
      dataIndex: 'order_number',
      key: 'order_number',
      render: (orderNumber, order) => orderNumber || order.order_id,
    },
    {
      title: 'Customer',
      dataIndex: 'customer_name',
      key: 'customer_name',
      render: (name) => name || 'Guest',
    },
    {
      title: 'Restaurant',
      dataIndex: 'restaurant_name',
      key: 'restaurant_name',
    },
    {
      title: 'Deliver by',
      dataIndex: 'requested_time',
      key: 'requested_time',
    },
  ], []);

  const selectedStoreLabel = useMemo(() => {
    if (!restaurantIds.length) {
      return 'all stores';
    }
    const names = restaurantOptions
      .filter((option) => restaurantIds.includes(option.value))
      .map((option) => option.label);
    return names.join(', ') || 'the selected stores';
  }, [restaurantIds, restaurantOptions]);

  const previewDateLabel = useMemo(() => {
    if (!dateRange?.[0] || !dateRange?.[1]) {
      return 'the current filters';
    }
    const start = dateRange[0].format('MMM D, YYYY');
    const end = dateRange[1].format('MMM D, YYYY');
    return start === end ? start : `${start} – ${end}`;
  }, [dateRange]);

  const columns = useMemo(() => [
    {
      title: 'Order',
      dataIndex: 'order_number',
      key: 'order_number',
      render: (orderNumber, order) => orderNumber || order.order_id,
    },
    {
      title: 'Customer',
      dataIndex: 'customer_name',
      key: 'customer_name',
      render: (name) => name || 'Guest',
    },
    {
      title: 'Restaurant',
      dataIndex: 'restaurant_name',
      key: 'restaurant_name',
    },
    {
      title: 'Deliver by',
      dataIndex: 'requested_time',
      key: 'requested_time',
    },
    {
      title: '',
      key: 'print',
      width: 110,
      render: (_, order) => (
        <Button
          icon={<PrinterOutlined />}
          loading={printingOrderId === order.order_id}
          disabled={Boolean(printingOrderId) && printingOrderId !== order.order_id}
          onClick={() => handlePrintOrder(order.order_id)}
        >
          Print
        </Button>
      ),
    },
  ], [handlePrintOrder, printingOrderId]);

  return (
    <div className="prep-sheet-root">
      <div className="prep-sheet-page-header">
        <nav className="prep-sheet-breadcrumb" aria-label="Breadcrumb">
          <Link to="/console/prep-kitchen/ingredients">Prep &amp; kitchen</Link>
          <span className="prep-sheet-breadcrumb-sep">&gt;</span>
          <span>Handoff sheets</span>
        </nav>
        <Flex justify="space-between" align="flex-start" wrap gap={16}>
          <div>
            <Typography.Title level={2} className="prep-sheet-page-title">
              Handoff sheets
            </Typography.Title>
            <Typography.Text type="secondary">
              Catering delivery orders. Select the orders to print.
            </Typography.Text>
          </div>
          <Button
            icon={<PrinterOutlined />}
            type="primary"
            onClick={openPrintPreview}
            loading={printPreviewLoading}
            disabled={!organizationId || Boolean(filterError) || printTooManyOrders || !selectedCount}
          >
            {selectedCount ? `Print ${selectedCount}` : 'Print'}
          </Button>
        </Flex>
      </div>

      <div className="prep-sheet-filter-bar">
        <div className="prep-sheet-filter-row">
          <div className="prep-sheet-filter-field prep-sheet-filter-field--date">
            <span className="prep-sheet-filter-label">Date range</span>
            <RangePicker
              value={dateRange}
              onChange={(range) => setDateRange(range || [null, null])}
              style={{ width: '100%' }}
            />
          </div>
          <div className="prep-sheet-filter-field prep-sheet-filter-field--store">
            <span className="prep-sheet-filter-label">Store</span>
            <Select
              mode="multiple"
              allowClear
              showSearch
              optionFilterProp="label"
              filterOption={(input, option) => (
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              )}
              placeholder="All stores"
              value={restaurantIds}
              options={restaurantOptions}
              onChange={setRestaurantIds}
              style={{ width: '100%' }}
            />
          </div>
        </div>
      </div>

      {filterError && (
        <Alert
          type="error"
          showIcon
          message={filterError}
          style={{ marginBottom: 16 }}
        />
      )}

      {printTooManyOrders && (
        <Alert
          type="warning"
          showIcon
          message={printLimitMessage}
          style={{ marginBottom: 16 }}
        />
      )}

      {handoffError && (
        <Alert
          type="warning"
          showIcon
          message="Unable to load handoff sheets."
          style={{ marginBottom: 16 }}
        />
      )}

      {handoffLoading ? (
        <Flex align="center" justify="center" className="h-[35vh]">
          <Spin size="large" />
        </Flex>
      ) : null}

      {!handoffLoading && orders.length === 0 ? (
        <Empty description="No catering delivery orders for this date range." />
      ) : null}

      {!handoffLoading && orders.length > 0 && (
        <>
          <Table
            className="handoff-sheet-table"
            rowKey="order_id"
            columns={columns}
            dataSource={orders}
            pagination={false}
            rowSelection={{
              selectedRowKeys: orders
                .filter((order) => isOrderSelected(order.order_id))
                .map((order) => order.order_id),
              onSelect: (order, selected) => {
                setOrdersSelected([order.order_id], selected);
              },
              columnTitle: (
                <Checkbox
                  checked={page === 1
                    ? totalCount > 0 && selectedCount === totalCount
                    : orders.length > 0 && orders.every((order) => isOrderSelected(order.order_id))}
                  indeterminate={page === 1
                    ? selectedCount > 0 && selectedCount < totalCount
                    : orders.some((order) => isOrderSelected(order.order_id))
                      && !orders.every((order) => isOrderSelected(order.order_id))}
                  aria-label={page === 1
                    ? 'Select every order in the current filters'
                    : 'Select every order on this page'}
                  onChange={(event) => {
                    if (page === 1) {
                      setSelection(event.target.checked
                        ? { all: true, picked: new Set(), skipped: new Set() }
                        : { all: false, picked: new Set(), skipped: new Set() });
                      return;
                    }
                    setOrdersSelected(
                      orders.map((order) => order.order_id),
                      event.target.checked,
                    );
                  }}
                />
              ),
            }}
          />
          {totalCount > HANDOFF_PAGE_SIZE && (
            <Flex justify="center" className="prep-sheet-pagination">
              <Pagination
                current={page}
                pageSize={HANDOFF_PAGE_SIZE}
                total={totalCount}
                showSizeChanger={false}
                onChange={setPage}
              />
            </Flex>
          )}
        </>
      )}

      <Modal
        title="Print these handoff sheets"
        open={printPreviewOpen}
        onCancel={() => {
          if (!printAllLoading) {
            setPrintPreviewOpen(false);
          }
        }}
        width={1080}
        footer={[
          <Button
            key="cancel"
            onClick={() => setPrintPreviewOpen(false)}
            disabled={printAllLoading}
          >
            Cancel
          </Button>,
          <Button
            key="print"
            type="primary"
            icon={<PrinterOutlined />}
            loading={printAllLoading}
            disabled={printPreviewLoading || !printPreviewOrders.length}
            onClick={confirmPrintAll}
          >
            Print
            {printPreviewOrders.length ? ` ${printPreviewOrders.length}` : ''}
          </Button>,
        ]}
      >
        <Typography.Paragraph type="secondary">
          {printPreviewLoading
            ? 'Loading every catering delivery order in the current filters.'
            : `${printPreviewOrders.length === totalCount
              ? `${printPreviewOrders.length} ${printPreviewOrders.length === 1 ? 'order' : 'orders'}`
              : `${printPreviewOrders.length} of ${totalCount} orders`} for ${previewDateLabel} at ${selectedStoreLabel} will be printed together.`}
        </Typography.Paragraph>
        {printPreviewLoading ? (
          <Flex align="center" justify="center" style={{ minHeight: 160 }}>
            <Spin />
          </Flex>
        ) : (
          <Table
            className="handoff-print-preview"
            rowKey="order_id"
            size="small"
            columns={previewColumns}
            dataSource={printPreviewOrders}
            pagination={false}
            scroll={{ y: 360, x: 'max-content' }}
          />
        )}
      </Modal>
    </div>
  );
}

export default HandoffSheets;
